/**
 * Jam Session Panel
 * Active jam session controls, participants, and WebRTC audio streaming
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  X,
  Radio,
  Users,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Crown,
  Eye,
  Play,
  Square,
  Copy,
  Check,
  Settings,
  MessageCircle,
  Send,
  LogOut,
  Circle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Music2,
  Headphones,
  AlertCircle,
  Gauge,
} from 'lucide-react';
import { toast } from 'sonner';
import { LatencySettings } from './LatencySettings';
import { SessionRecording } from './SessionRecording';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL?.replace(/^http/, 'ws');

// Role badges
const RoleBadge = ({ role }) => {
  const config = {
    host: { bg: 'bg-orange-500', icon: Crown, label: 'Host' },
    performer: { bg: 'bg-emerald-500', icon: Mic, label: 'Performer' },
    listener: { bg: 'bg-blue-500', icon: Headphones, label: 'Listener' },
  };
  
  const { bg, icon: Icon, label } = config[role] || config.listener;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${bg} text-white`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// Participant card
const ParticipantCard = ({ 
  participant, 
  isCurrentUser, 
  isHost, 
  onMute, 
  onKick, 
  onChangeRole,
  audioLevel = 0 
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`p-3 rounded-xl border transition-all ${
        isCurrentUser 
          ? 'border-orange-500/30 bg-orange-500/10' 
          : 'border-zinc-700 bg-zinc-800/50'
      }`}
      data-testid={`participant-${participant.user_id}`}
    >
      <div className="flex items-center gap-3">
        {/* Avatar with audio level indicator */}
        <div className="relative">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
            style={{ backgroundColor: participant.color }}
          >
            {participant.user_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          {/* Audio level ring */}
          {audioLevel > 0.1 && (
            <div 
              className="absolute inset-0 rounded-full border-2 border-emerald-400 animate-pulse"
              style={{ opacity: Math.min(audioLevel * 2, 1) }}
            />
          )}
          {/* Recording indicator */}
          {participant.is_recording && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
              <Circle className="w-2 h-2 fill-white text-white" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-white truncate">
              {participant.user_name}
              {isCurrentUser && <span className="text-zinc-400 text-xs ml-1">(you)</span>}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <RoleBadge role={participant.role} />
            {participant.is_muted && (
              <span className="text-xs text-red-400 flex items-center gap-1">
                <MicOff className="w-3 h-3" /> Muted
              </span>
            )}
          </div>
        </div>

        {/* Host controls */}
        {isHost && !isCurrentUser && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onMute?.(participant.user_id)}
              className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
              title={participant.is_muted ? 'Unmute' : 'Mute'}
            >
              {participant.is_muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              onClick={() => onKick?.(participant.user_id)}
              className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              title="Remove"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Chat message
const ChatMessage = ({ message, isOwn }) => (
  <div className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
    <div
      className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white"
      style={{ backgroundColor: message.user_color }}
    >
      {message.user_name?.charAt(0)?.toUpperCase()}
    </div>
    <div className={`max-w-[70%] ${isOwn ? 'text-right' : ''}`}>
      <div className="text-xs text-zinc-500">
        {message.user_name}
      </div>
      <div className={`px-3 py-1.5 rounded-lg text-sm ${
        isOwn ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-200'
      }`}>
        {message.message}
      </div>
    </div>
  </div>
);

export const JamSessionPanel = ({
  session,
  userId,
  userName,
  onClose,
  onTransportSync,
  onTrackUpdate,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [participants, setParticipants] = useState(session?.participants || []);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioLevels, setAudioLevels] = useState({});
  const [showLatencySettings, setShowLatencySettings] = useState(false);
  const [sessionSettings, setSessionSettings] = useState(session?.settings || {});
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const peerConnectionsRef = useRef({});
  const localStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);

  const currentParticipant = participants.find(p => p.user_id === userId);
  const isHost = currentParticipant?.role === 'host';

  // Connect to WebSocket
  useEffect(() => {
    if (!session?.id || !userId) return;

    const connect = () => {
      const ws = new WebSocket(`${WS_URL}/api/jam-sessions/ws/${session.id}/${userId}`);

      ws.onopen = () => {
        setIsConnected(true);
      };

      ws.onclose = () => {
        setIsConnected(false);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      wsRef.current = ws;
    };

    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      // Clean up peer connections
      Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
      peerConnectionsRef.current = {};
      // Clean up audio
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [session?.id, userId]);

  // Handle incoming messages
  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'session_state':
        setParticipants(data.session?.participants || []);
        break;

      case 'participant_connected':
      case 'participant_disconnected':
      case 'participant_kicked':
      case 'role_changed':
      case 'mute_changed':
      case 'recording_changed':
      case 'track_assigned':
        setParticipants(data.participants || []);
        break;

      case 'session_started':
        toast.success('Jam session started!');
        break;

      case 'session_ended':
        toast.info('Jam session ended');
        onClose?.();
        break;

      case 'kicked':
        toast.error(`You were removed: ${data.reason}`);
        onClose?.();
        break;

      case 'transport_sync':
        onTransportSync?.(data.action, data.position);
        toast.info(`${data.user_name} ${data.action === 'play' ? 'started playback' : 'stopped playback'}`);
        break;

      case 'chat_message':
        setMessages(prev => [...prev, data]);
        break;

      case 'audio_level_update':
        setAudioLevels(prev => ({
          ...prev,
          [data.user_id]: data.level
        }));
        break;

      // WebRTC signaling
      case 'webrtc_offer':
        handleWebRTCOffer(data);
        break;

      case 'webrtc_answer':
        handleWebRTCAnswer(data);
        break;

      case 'webrtc_ice_candidate':
        handleICECandidate(data);
        break;

      case 'track_update':
        onTrackUpdate?.(data);
        break;

      // Latency-related messages
      case 'pong':
        // Dispatch event for latency settings component
        window.dispatchEvent(new CustomEvent('jam-pong', { detail: data }));
        break;

      case 'latency_updated':
        setParticipants(data.participants || []);
        break;

      case 'settings_updated':
        setSessionSettings(data.settings || {});
        toast.info('Session settings updated');
        break;

      case 'latency_test_request':
        // Respond to latency test
        send({
          type: 'latency_test_response',
          target_user_id: data.from_user_id,
          original_timestamp: data.timestamp,
          response_timestamp: Date.now()
        });
        break;

      // Recording-related messages
      case 'recording_started':
        setSessionSettings(prev => ({ ...prev, recording: data.recording }));
        toast.success(`${data.user_name} started recording`);
        break;

      case 'recording_stopped':
        setSessionSettings(prev => ({ ...prev, recording: data.recording }));
        toast.info('Recording stopped');
        break;

      case 'recording_paused':
        setSessionSettings(prev => ({ ...prev, recording: data.recording }));
        break;

      case 'recording_resumed':
        setSessionSettings(prev => ({ ...prev, recording: data.recording }));
        break;

      case 'clip_recorded':
        setSessionSettings(prev => ({
          ...prev,
          recording: {
            ...prev.recording,
            clips: [...(prev.recording?.clips || []), data.clip]
          }
        }));
        if (data.user_id !== userId) {
          toast.info(`${data.user_name} recorded a clip`);
        }
        break;

      case 'clip_deleted':
        setSessionSettings(prev => ({
          ...prev,
          recording: {
            ...prev.recording,
            clips: (prev.recording?.clips || []).filter(c => c.id !== data.clip_id)
          }
        }));
        break;

      case 'clip_synced':
        setSessionSettings(prev => ({
          ...prev,
          recording: {
            ...prev.recording,
            clips: (prev.recording?.clips || []).map(c =>
              c.id === data.clip_id ? { ...c, audio_url: data.audio_url } : c
            )
          }
        }));
        break;

      case 'session_exporting':
        toast.info('Exporting session to project...');
        break;

      default:
    }
  }, [onClose, onTransportSync, onTrackUpdate, userId]);

  // Send message via WebSocket
  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  // Initialize local audio stream
  const initializeAudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;

      // Set up audio analysis
      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      source.connect(analyserRef.current);

      // Start audio level monitoring
      monitorAudioLevel();

      return stream;
    } catch (err) {
      console.error('Error accessing microphone:', err);
      toast.error('Could not access microphone');
      return null;
    }
  };

  // Monitor local audio level
  const monitorAudioLevel = () => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    
    const check = () => {
      if (!analyserRef.current) return;
      
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const level = average / 255;

      // Send audio level to other participants
      if (level > 0.05 && !isMuted) {
        send({ type: 'audio_level', level });
      }

      requestAnimationFrame(check);
    };

    check();
  };

  // WebRTC: Create peer connection
  const createPeerConnection = (targetUserId) => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ]
    });

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        send({
          type: 'webrtc_ice_candidate',
          target_user_id: targetUserId,
          candidate: event.candidate
        });
      }
    };

    pc.ontrack = (event) => {
      // Play remote audio
      const audio = new Audio();
      audio.srcObject = event.streams[0];
      audio.play().catch(console.error);
    };

    peerConnectionsRef.current[targetUserId] = pc;
    return pc;
  };

  // WebRTC: Handle incoming offer
  const handleWebRTCOffer = async (data) => {
    const pc = createPeerConnection(data.from_user_id);
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    send({
      type: 'webrtc_answer',
      target_user_id: data.from_user_id,
      sdp: answer
    });
  };

  // WebRTC: Handle incoming answer
  const handleWebRTCAnswer = async (data) => {
    const pc = peerConnectionsRef.current[data.from_user_id];
    if (pc) {
      await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
    }
  };

  // WebRTC: Handle ICE candidate
  const handleICECandidate = async (data) => {
    const pc = peerConnectionsRef.current[data.from_user_id];
    if (pc && data.candidate) {
      await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
    }
  };

  // Start streaming audio to all participants
  const startAudioStream = async () => {
    const stream = await initializeAudio();
    if (!stream) return;

    // Create peer connections and send offers to all other participants
    for (const participant of participants) {
      if (participant.user_id !== userId) {
        const pc = createPeerConnection(participant.user_id);
        
        stream.getTracks().forEach(track => {
          pc.addTrack(track, stream);
        });

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        send({
          type: 'webrtc_offer',
          target_user_id: participant.user_id,
          sdp: offer
        });
      }
    }

    toast.success('Audio streaming started');
  };

  // Toggle mute
  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !newMuted;
      });
    }

    send({ type: 'toggle_mute' });
  };

  // Toggle recording
  const toggleRecording = () => {
    setIsRecording(!isRecording);
    send({ type: 'toggle_recording' });
  };

  // Send transport command
  const sendTransport = (action, position = 0) => {
    send({ type: 'transport', action, position });
  };

  // Send chat message
  const sendChat = () => {
    if (!newMessage.trim()) return;
    send({ type: 'chat', message: newMessage.trim() });
    setNewMessage('');
  };

  // Copy invite code
  const copyInviteCode = () => {
    navigator.clipboard.writeText(session?.invite_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Invite code copied!');
  };

  // Host: Start session
  const startSession = () => {
    send({ type: 'start_session' });
  };

  // Host: End session
  const endSession = () => {
    if (window.confirm('Are you sure you want to end this session for everyone?')) {
      send({ type: 'end_session' });
    }
  };

  // Host: Kick participant
  const kickParticipant = (targetUserId) => {
    if (window.confirm('Remove this participant?')) {
      send({ type: 'kick_participant', target_user_id: targetUserId });
    }
  };

  // Leave session
  const leaveSession = () => {
    if (isHost) {
      if (!window.confirm('As host, leaving will transfer ownership. Continue?')) return;
    }
    onClose?.();
  };

  // Auto-scroll chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="w-80 bg-zinc-900/95 backdrop-blur-md border-l border-zinc-700 flex flex-col h-full"
      data-testid="jam-session-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-orange-500/10 to-amber-500/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
            <Radio className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white truncate">{session?.name}</h3>
            <div className="flex items-center gap-1 text-xs">
              {isConnected ? (
                <>
                  <Circle className="w-2 h-2 fill-emerald-400 text-emerald-400" />
                  <span className="text-emerald-400">Live</span>
                </>
              ) : (
                <>
                  <Loader2 className="w-3 h-3 animate-spin text-amber-400" />
                  <span className="text-amber-400">Connecting...</span>
                </>
              )}
              <span className="text-zinc-500 mx-1">•</span>
              <span className="text-zinc-400">{participants.length} participants</span>
            </div>
          </div>
          <button
            onClick={leaveSession}
            className="p-1 text-zinc-400 hover:text-white transition-colors"
            title="Leave session"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Invite Code */}
      <div className="px-4 py-2 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <div className="flex-1 px-3 py-2 bg-zinc-800 rounded-lg font-mono text-center text-lg tracking-widest text-white">
            {session?.invite_code}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={copyInviteCode}
            className="border-zinc-700"
            data-testid="copy-code-btn"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-xs text-zinc-500 text-center mt-1">Share this code to invite others</p>
      </div>

      {/* Audio Controls */}
      <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-800/30">
        <div className="flex items-center gap-2">
          <Button
            variant={isMuted ? "destructive" : "outline"}
            size="sm"
            onClick={toggleMute}
            className={isMuted ? "" : "border-zinc-700"}
            data-testid="mute-btn"
          >
            {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </Button>
          
          {currentParticipant?.role !== 'listener' && (
            <Button
              variant={isRecording ? "destructive" : "outline"}
              size="sm"
              onClick={toggleRecording}
              className={`flex-1 ${isRecording ? "" : "border-zinc-700"}`}
              data-testid="record-btn"
            >
              {isRecording ? (
                <>
                  <Square className="w-4 h-4 mr-2" />
                  Stop Recording
                </>
              ) : (
                <>
                  <Circle className="w-4 h-4 mr-2 fill-current" />
                  Record
                </>
              )}
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={startAudioStream}
            className="border-zinc-700"
            title="Start audio streaming"
            data-testid="stream-btn"
          >
            <Headphones className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowLatencySettings(true)}
            className="border-zinc-700"
            title="Latency Settings"
            data-testid="latency-settings-btn"
          >
            <Gauge className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Participants */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-white">Participants</span>
          <span className="text-xs text-zinc-500">{participants.length}/{session?.max_participants}</span>
        </div>
        
        <div className="space-y-2">
          <AnimatePresence>
            {participants.map((participant) => (
              <ParticipantCard
                key={participant.user_id}
                participant={participant}
                isCurrentUser={participant.user_id === userId}
                isHost={isHost}
                onKick={kickParticipant}
                audioLevel={audioLevels[participant.user_id] || 0}
              />
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Session Recording Section */}
      <SessionRecording
        session={{ ...session, recording: sessionSettings.recording || session?.recording }}
        userId={userId}
        onSend={send}
        onExport={(recording) => {
          // Could trigger project creation with recorded clips
        }}
        audioStream={localStreamRef.current}
      />

      {/* Chat Toggle */}
      <button
        onClick={() => setShowChat(!showChat)}
        className="px-4 py-2 border-t border-zinc-800 flex items-center justify-between text-sm text-zinc-400 hover:bg-zinc-800/50"
      >
        <span className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4" />
          Chat
          {messages.length > 0 && (
            <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center">
              {messages.length}
            </span>
          )}
        </span>
        {showChat ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
      </button>

      {/* Chat Panel */}
      <AnimatePresence>
        {showChat && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 200 }}
            exit={{ height: 0 }}
            className="border-t border-zinc-800 flex flex-col overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2">
              {messages.length === 0 ? (
                <div className="text-center py-4 text-zinc-500 text-sm">
                  No messages yet
                </div>
              ) : (
                messages.map((msg, i) => (
                  <ChatMessage key={i} message={msg} isOwn={msg.user_id === userId} />
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="p-2 border-t border-zinc-800">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendChat()}
                  placeholder="Type a message..."
                  className="flex-1 bg-zinc-800 border-zinc-700 text-sm"
                  data-testid="chat-input"
                />
                <Button
                  size="sm"
                  onClick={sendChat}
                  disabled={!newMessage.trim()}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Host Controls */}
      {isHost && (
        <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-800/30">
          <div className="flex gap-2">
            {session?.status === 'lobby' ? (
              <Button
                onClick={startSession}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                data-testid="start-session-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Start Session
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={endSession}
                className="flex-1"
                data-testid="end-session-btn"
              >
                <Square className="w-4 h-4 mr-2" />
                End Session
              </Button>
            )}
          </div>
        </div>
      )}
      
      {/* Latency Settings Modal */}
      <LatencySettings
        isOpen={showLatencySettings}
        onClose={() => setShowLatencySettings(false)}
        session={{ ...session, settings: sessionSettings, participants }}
        userId={userId}
        onSend={send}
        currentLatency={currentParticipant}
      />
    </motion.div>
  );
};

export default JamSessionPanel;
